/**
 * Formatters & Utils
 * Tout ce qui formate ou transforme une valeur pour l'affichage ou la DB.
 * Organisé par thème : Time / Duration / Date / User / Calendar / Slot / Grid
 */

import {
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { fr } from "date-fns/locale";

import { addMonths } from "date-fns";

// ─── Time utils ─────────────────────────────────────────────────

/** "HH:MM" → minutes depuis minuit */
export function timeToMinutes(timeStr) {
  if (typeof timeStr !== "string") return 0;
  const [h, m] = timeStr.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return 0;
  return h * 60 + m;
}

/** Minutes depuis minuit → "HH:MM" */
export function minutesToTime(minutes) {
  if (typeof minutes !== "number" || isNaN(minutes)) return "00:00";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Affichage : strips seconds si présents */
export function formatTime(timeStr) {
  if (!timeStr) return "";
  return String(timeStr).slice(0, 5);
}

/** Accepte "2024-01-15T10:30:00" ou "10:30" ou "10:30:00" → "HH:MM" */
export function formatTimeFlexible(value) {
  if (!value) return "";
  try {
    if (value.includes("T")) {
      const d = new Date(value);
      return `${String(d.getHours()).padStart(2, "0")}:${String(
        d.getMinutes(),
      ).padStart(2, "0")}`;
    }
    return value.slice(0, 5);
  } catch {
    return value;
  }
}

/** DB → <input type="time"> : "10:30:00" → "10:30" */
export function formatTimeForInput(timeStr) {
  if (!timeStr) return "";
  return String(timeStr).slice(0, 5);
}

/** <input type="time"> → DB : "10:30" → "10:30:00" */
export function formatTimeForDatabase(timeStr) {
  if (!timeStr) return "";
  return timeStr.length === 8 ? timeStr : `${timeStr}:00`;
}

/** Normalize n'importe quelle valeur en "HH:MM" */
export function normalizeTimeToHHMM(time) {
  if (!time) return "00:00";
  if (typeof time === "string") {
    const parts = time.split(":");
    if (parts.length >= 2)
      return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
  }
  return "00:00";
}

// ─── Duration utils ─────────────────────────────────────────────

/** "08:00", "09:30" → 90 */
export function calculateDurationMinutes(startTime, endTime) {
  if (!startTime || !endTime) return 0;
  return Math.max(0, timeToMinutes(endTime) - timeToMinutes(startTime));
}

/** 90 → "1h30min" | 60 → "1h" | 45 → "45min" */
export function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h${m}min`;
}

// ─── Date utils ─────────────────────────────────────────────────

/** Formate une date avec date-fns (locale fr) */
export function formatDate(date, formatStr = "dd/MM/yyyy") {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, formatStr, { locale: fr });
}

/** Formate une date + heure avec date-fns (locale fr) */
export function formatDateTime(date, formatStr = "dd/MM/yyyy HH:mm") {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, formatStr, { locale: fr });
}

/** Aujourd'hui en "YYYY-MM-DD" */
export function getTodayISO() {
  return new Date().toISOString().split("T")[0];
}

/** Maintenant en ISO full */
export function getNowISO() {
  return new Date().toISOString();
}

// ─── User utils ─────────────────────────────────────────────────

/** Initiales depuis un objet user { first_name, last_name } */
export function getUserInitials(user) {
  if (!user) return "?";
  const f = user.first_name?.[0] || "";
  const l = user.last_name?.[0] || "";
  return (f + l).toUpperCase() || "?";
}

// ─── Calendar / Date ranges ─────────────────────────────────────

export function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseDate(str) {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function getMondayOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // lundi = jour 1
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getDaysOfWeek(monday) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

export function getDaysOfMonth(year, month) {
  const firstDay = new Date(year, month, 1);
  const start = getMondayOfWeek(firstDay);
  const days = [];
  const d = new Date(start);
  while (d.getMonth() <= month || days.length % 7 !== 0) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
    if (days.length > 42) break; // max 6 semaines
  }
  return days;
}

export function getDateRange(view, currentDate) {
  const date =
    currentDate instanceof Date ? currentDate : new Date(currentDate);

  if (view === "week") {
    const monday = getMondayOfWeek(date);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: toDateStr(monday), to: toDateStr(sunday) };
  }

  if (view === "timeline") {
    const end = new Date(date);
    end.setDate(end.getDate() + 60);
    return { from: toDateStr(date), to: toDateStr(end) };
  }

  // month
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from: toDateStr(first), to: toDateStr(last) };
}

// ─── Slot utils ─────────────────────────────────────────────────

/** Normalize start_time / end_time sur un slot venant de l'API */
export function enrichSlot(slot) {
  if (!slot) return slot;
  return {
    ...slot,
    start_time: normalizeTimeToHHMM(slot.start_time),
    end_time: normalizeTimeToHHMM(slot.end_time),
  };
}

// ─── Calendar positioning ──────────────────────────────────────

/** CSS top/height pour un bloc horaire sur une grille calendrier */
export function calculateSelectionStyle(
  selectionStart,
  selectionEnd,
  HOUR_HEIGHT = 60,
  START_HOUR = 8,
  END_HOUR = 22,
) {
  if (!selectionStart || !selectionEnd) return null;

  const startMin = timeToMinutes(selectionStart);
  const endMin = timeToMinutes(selectionEnd);
  const dayStart = START_HOUR * 60;
  const dayEnd = END_HOUR * 60;

  const lo = Math.min(startMin, endMin);
  const hi = Math.max(startMin, endMin);
  if (hi <= dayStart || lo >= dayEnd) return null;

  const clampedStart = Math.max(lo, dayStart);
  const clampedEnd = Math.min(hi, dayEnd);

  return {
    top: `${(clampedStart - dayStart) * (HOUR_HEIGHT / 60)}px`,
    height: `${(clampedEnd - clampedStart) * (HOUR_HEIGHT / 60)}px`,
  };
}

export function getCalendarRange(view, currentDate) {
  const date = new Date(currentDate);

  switch (view) {
    case "week":
      return {
        from: startOfWeek(date, { weekStartsOn: 1 }),
        to: endOfWeek(date, { weekStartsOn: 1 }),
      };

    case "month":
      return {
        from: startOfMonth(date),
        to: endOfMonth(date),
      };

    case "timeline":
    default:
      return {
        from: startOfWeek(date, { weekStartsOn: 1 }),
        to: addMonths(date, 3), // aujourd'hui + 3 mois
      };
  }
}
